import { Link } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ProgressBar } from "@/components/shared/progress-bar";
import { getStatusLabel, getStatusVariant, getLevelLabel } from "@/lib/status-machine";
import { getFiscalLabel } from "@/lib/fiscal-year";
import { formatDueDate } from "@/lib/date";
import type { FullAlignmentNode } from "@/hooks/use-full-alignment";

interface NodeDetailDialogProps {
  node: FullAlignmentNode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  year?: number;
}

const HORIZON_LABELS: Record<string, string> = {
  "7_year": "7-Year",
  "3_year": "3-Year",
  "18_month": "18-Month",
};

const NODE_TYPE_LABELS: Record<string, string> = {
  college_strategic_pillar: "College Strategic Pillar",
  college_goal: "College Objective",
  focus_area: "ITS Focus Area",
  tactic: "ITS Strategic Initiative",
  objective: "Objective",
  key_result: "Key Result",
};

const KR_TYPE_LABELS: Record<string, string> = {
  percentage: "Percentage",
  binary: "Binary (Yes/No)",
  metric: "Metric",
};

function ObjectiveDetail({ node, year }: { node: FullAlignmentNode; year?: number }) {
  const krs = node.children.filter((c) => c.node_type === "key_result");
  const childObjectives = node.children.filter((c) => c.node_type === "objective");
  const pct = node.completion_percent ?? 0;

  return (
    <div className="space-y-4">
      {/* Metadata row */}
      <div className="flex flex-wrap items-center gap-2">
        {node.level && (
          <Badge variant="meta-level">{getLevelLabel(node.level)}</Badge>
        )}
        {node.status && (
          <Badge variant={getStatusVariant(node.status)}>
            {getStatusLabel(node.status)}
          </Badge>
        )}
        {node.type && (
          <Badge variant="meta-type">{node.type}</Badge>
        )}
      </div>

      {/* Owner + progress */}
      <div className="flex items-center justify-between">
        {node.owner_name && (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Owner: {node.owner_name}
          </p>
        )}
        <div className="flex items-center gap-2">
          <ProgressBar value={pct} className="w-24" />
          <span className="text-sm font-medium">{Math.round(pct)}%</span>
        </div>
      </div>

      {/* Key Results */}
      {krs.length > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="mb-2 text-sm font-semibold">
              Key Results ({krs.length})
            </h3>
            <div className="space-y-3">
              {krs.map((kr) => (
                <KrRow key={kr.id} kr={kr} year={year} />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Child Objectives */}
      {childObjectives.length > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="mb-2 text-sm font-semibold">
              Child Objectives ({childObjectives.length})
            </h3>
            <div className="space-y-2">
              {childObjectives.map((child) => (
                <div
                  key={child.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{child.title}</p>
                    {child.owner_name && (
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        {child.owner_name}
                      </p>
                    )}
                  </div>
                  <div className="ml-3 flex items-center gap-2">
                    {child.status && (
                      <Badge
                        variant={getStatusVariant(child.status)}
                        className="text-[10px] px-1.5 py-0"
                      >
                        {getStatusLabel(child.status)}
                      </Badge>
                    )}
                    <span className="text-xs font-medium">
                      {Math.round(child.completion_percent ?? 0)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Link to detail page */}
      <Separator />
      <Link
        to="/objectives/$id"
        params={{ id: node.id }}
        className="inline-block text-sm font-medium text-[hsl(var(--primary))] underline-offset-4 hover:underline"
      >
        Open full detail page
      </Link>
    </div>
  );
}

function KrRow({ kr, year }: { kr: FullAlignmentNode; year?: number }) {
  const pct = kr.completion_percent ?? 0;

  return (
    <div className="rounded-md border px-3 py-2">
      <p className="text-sm leading-snug">{kr.title}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
        {kr.quarter && <span>{kr.quarter}{year ? (() => { const fy = getFiscalLabel(kr.quarter, year); return fy ? ` (${fy})` : ""; })() : ""}</span>}
        {kr.due_date && <span>Due: {formatDueDate(kr.due_date)}</span>}
        {kr.kr_type && <span>{KR_TYPE_LABELS[kr.kr_type] ?? kr.kr_type}</span>}
        {kr.kr_type === "metric" && kr.target_value != null && (
          <span>
            {kr.current_value ?? 0} / {kr.target_value}
          </span>
        )}
        {kr.status && (
          <Badge
            variant={getStatusVariant(kr.status)}
            className="text-[10px] px-1.5 py-0"
          >
            {getStatusLabel(kr.status)}
          </Badge>
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <ProgressBar value={pct} className="flex-1" />
        <span className="text-xs font-medium">{Math.round(pct)}%</span>
      </div>
    </div>
  );
}

function KrDetail({ node, year }: { node: FullAlignmentNode; year?: number }) {
  const pct = node.completion_percent ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">KR</Badge>
        {node.kr_type && (
          <Badge variant="outline">
            {KR_TYPE_LABELS[node.kr_type] ?? node.kr_type}
          </Badge>
        )}
        {node.status && (
          <Badge variant={getStatusVariant(node.status)}>
            {getStatusLabel(node.status)}
          </Badge>
        )}
        {node.quarter && <Badge variant="outline">{node.quarter}{year ? (() => { const fy = getFiscalLabel(node.quarter, year); return fy ? ` ${fy}` : ""; })() : ""}</Badge>}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        {node.due_date && (
          <div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Due date</p>
            <p className="font-medium">{formatDueDate(node.due_date)}</p>
          </div>
        )}
        {node.kr_type === "metric" && node.target_value != null && (
          <div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Progress</p>
            <p className="font-medium">
              {node.current_value ?? 0} / {node.target_value}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <ProgressBar value={pct} className="flex-1" />
        <span className="text-sm font-medium">{Math.round(pct)}%</span>
      </div>
    </div>
  );
}

function StrategicDetail({ node }: { node: FullAlignmentNode }) {
  const childCounts = new Map<string, number>();
  for (const child of node.children) {
    const label = NODE_TYPE_LABELS[child.node_type] ?? child.node_type;
    childCounts.set(label, (childCounts.get(label) ?? 0) + 1);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="info">{NODE_TYPE_LABELS[node.node_type]}</Badge>
        {node.horizon && (
          <Badge variant="outline">
            {HORIZON_LABELS[node.horizon] ?? node.horizon}
          </Badge>
        )}
        {node.reference_code && (
          <Badge variant="outline" className="font-mono">
            {node.reference_code}
          </Badge>
        )}
      </div>

      {childCounts.size > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="mb-1 text-sm font-semibold">Contains</h3>
            <ul className="space-y-0.5 text-sm text-[hsl(var(--muted-foreground))]">
              {Array.from(childCounts.entries()).map(([label, count]) => (
                <li key={label}>
                  {count} {label}{count > 1 ? "s" : ""}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export function NodeDetailDialog({ node, open, onOpenChange, year }: NodeDetailDialogProps) {
  if (!node) return null;

  const typeLabel = NODE_TYPE_LABELS[node.node_type] ?? node.node_type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogDescription>{typeLabel}</DialogDescription>
          <DialogTitle className="text-lg leading-snug">
            {node.title}
          </DialogTitle>
        </DialogHeader>

        {node.node_type === "objective" && <ObjectiveDetail node={node} year={year} />}
        {node.node_type === "key_result" && <KrDetail node={node} year={year} />}
        {node.node_type !== "objective" && node.node_type !== "key_result" && (
          <StrategicDetail node={node} />
        )}
      </DialogContent>
    </Dialog>
  );
}
